import { ProCard, ProDescriptions } from '@ant-design/pro-components';
import { history } from '@umijs/max';
import {
  Button,
  Form,
  Modal,
  message,
  Skeleton,
  Space,
  Tag,
  Timeline,
  Tooltip,
  Typography,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import React, {
  type CSSProperties,
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Empty from '@/components/QuietEmpty';
import {
  ActivityPanel,
  ActivityPanelFollowUps,
} from '@/modules/zy-crm/components/activity-panel/export';
import { AiDetailPanel } from '@/modules/zy-crm/components/ai-detail-panel';
import { AttachmentPanel } from '@/modules/zy-crm/components/attachment-panel';
import { BusinessModal } from '@/modules/zy-crm/components/business-modal';
import { BusinessTripRelationPanel } from '@/modules/zy-crm/components/business-trip-relation-panel';
import {
  type CalendarEventFormValues,
  calendarEventFormDefaultValues,
  createCalendarRelationFormValue,
  normalizeCalendarRelationPayload,
} from '@/modules/zy-crm/components/calendar-event-form';
import { ContactReferenceLink } from '@/modules/zy-crm/components/contact-detail-drawer';
import { ContactEditModal } from '@/modules/zy-crm/components/contact-edit-modal';
import { EntityTagEditor } from '@/modules/zy-crm/components/crm-tags';
import { CrmTaskRelationPanel } from '@/modules/zy-crm/components/crm-task-relation-panel';
import { CustomerReferenceLink } from '@/modules/zy-crm/components/customer-detail-drawer';
import { CustomerEditModal } from '@/modules/zy-crm/components/customer-edit-modal';
import { DetailNavigation } from '@/modules/zy-crm/components/detail-navigation';
import { DetailOperationLogSection } from '@/modules/zy-crm/components/detail-operation-log-section';
import { DetailSurface } from '@/modules/zy-crm/components/detail-surface';
import { FollowUpCreateModal } from '@/modules/zy-crm/components/follow-up-create-modal';
import LeadTransformModal from '@/modules/zy-crm/components/LeadTransformModal';
import { LeadEditModal } from '@/modules/zy-crm/components/lead-edit-modal';
import { MemberMultiPicker } from '@/modules/zy-crm/components/member-picker';
import { OpportunityReferenceLink } from '@/modules/zy-crm/components/opportunity-detail-drawer';
import { OpportunityEditModal } from '@/modules/zy-crm/components/opportunity-edit-modal';
import { ReferralSection as UnifiedReferralSection } from '@/modules/zy-crm/components/referral-section';
import { TeamMembersPanel } from '@/modules/zy-crm/components/team-members-panel';
import { useMemberDirectory } from '@/modules/zy-crm/member-directory';
import {
  defaultDetailSection,
  detailSectionLabel,
  normalizeDetailSections,
} from '@/modules/zy-crm/utils/detail-navigation';
import {
  formatCrmDate,
  resolveRelationText,
  toApiDateTime,
} from '@/modules/zy-crm/utils/display';
import type { LeadScopedViewMode } from '@/modules/zy-crm/utils/lead-view';
import {
  fetchAreaOptions,
  fetchContactOptions,
  fetchCustomerOptions,
  fetchIndustryOptions,
  fetchLeadOptions,
  fetchOpportunityOptions,
  fetchSourceChannelOptions,
  mergeOptions,
  type RelationOption,
  resolveRelationLabel,
} from '@/modules/zy-crm/utils/relation-options';
import {
  createCalendarEvent,
  queryCalendarEventsByBiz,
  updateCalendarEvent,
} from '@/services/bff/calendar.api';
import { queryLeadDrawerProfile } from '@/services/bff/lead.api';
import {
  createReferral,
  deleteReferral,
  updateReferral,
} from '@/services/bff/referral.api';
import type { CalendarEventItem } from '@/services/bff/types/calendar';
import type {
  LeadDrawerFollowUpItem,
  LeadDrawerLogItem,
  LeadDrawerProfile,
  LeadImportDetail,
  LeadItem,
} from '@/services/bff/types/lead';
import type {
  ReferralItem,
  ReferralReferrerType,
  ReferralTargetType,
} from '@/services/bff/types/referral';
import { createAppRequest } from '@/services/request';
import styles from './style.module.less';

const { Paragraph, Text, Title } = Typography;
const _appRequest = createAppRequest();
const _MemberMultiPickerField = MemberMultiPicker as unknown as React.FC<any>;

const formatMoney = (value?: string | number | null) => {
  if (value === undefined || value === null || value === '') {
    return '-';
  }
  const amount = Number(value);
  if (!Number.isFinite(amount)) {
    return '-';
  }
  return amount.toLocaleString('zh-CN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

type LeadDrawerSectionKey =
  | 'activities'
  | 'details'
  | 'followUps'
  | 'tasks'
  | 'businessTrips'
  | 'emails'
  | 'sms'
  | 'companyInfo'
  | 'teamMembers'
  | 'referrals'
  | 'attachments'
  | 'operationLogs';

type LeadDrawerOpenInput = {
  leadUuid?: string;
  leadName?: ReactNode;
  viewMode?: LeadScopedViewMode;
};

type LeadDetailDrawerContextValue = {
  openLead: (input: LeadDrawerOpenInput) => void;
};

type DrawerRequestError = Error & {
  status?: number;
};

type LeadReferenceLinkProps = {
  leadUuid?: string;
  leadName?: ReactNode;
  fallbackName?: ReactNode;
  emptyText?: ReactNode;
  canViewDetail?: boolean;
  viewMode?: LeadScopedViewMode;
  style?: CSSProperties;
};

type SectionPanelProps = {
  title: string;
  hint?: string;
  extra?: ReactNode;
  children: ReactNode;
};

type LeadDrawerLookups = {
  areaOptions: RelationOption[];
  customerOptions: RelationOption[];
  industryOptions: RelationOption[];
  sourceChannelOptions: RelationOption[];
  memberNameMap: Record<string, string>;
};

type ScheduleEditorState = {
  mode: 'create' | 'edit';
  item?: CalendarEventItem;
};

type IncomingReferralFormValues = {
  referrerType?: ReferralReferrerType;
  referrerUuid?: string;
  referralTime?: Dayjs;
  isPrimary?: boolean;
  profitShareRatio?: number;
  remark?: string;
};

type OutgoingReferralFormValues = {
  targetType?: ReferralTargetType;
  targetUuid?: string;
  referralTime?: Dayjs;
  remark?: string;
};

type LeadImportAttributionFields = {
  requestId?: string | null;
  marketingCampaignUuid?: string | null;
  marketingCampaignName?: string | null;
  campaignUuid?: string | null;
  campaignCode?: string | null;
  campaignCodeSnapshot?: string | null;
  campaignNameSnapshot?: string | null;
  campaignTouchpointUuid?: string | null;
  submitTime?: string | null;
  utmSource?: string | null;
  utmMedium?: string | null;
  utmCampaign?: string | null;
  clickId?: string | null;
};

const WEBSITE_LEAD_FIELD_LABELS: Record<string, string> = {
  name: '联系人姓名',
  phone: '手机号',
  email: '邮箱',
  companyName: '公司名称',
  jobTitle: '职位',
  industry: '行业',
  companySize: '公司规模',
  productInterest: '意向产品',
  timeline: '计划时间',
  budget: '预算',
  requirement: '需求说明',
};

function parseWebsiteLeadPayload(raw?: string) {
  if (!raw) return undefined;
  try {
    const parsed: unknown = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as {
          data?: Record<string, unknown>;
          context?: {
            scene?: string;
            sourceUrl?: string;
            metadata?: Record<string, unknown>;
          };
        })
      : undefined;
  } catch {
    return undefined;
  }
}

function formatWebsiteLeadValue(value: unknown) {
  if (value === null || value === undefined || value === '') return '-';
  if (Array.isArray(value)) return value.map(String).join('、');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

const LeadDetailDrawerContext =
  createContext<LeadDetailDrawerContextValue | null>(null);

const EMPTY_PAGE = {
  list: [],
  total: 0,
};

const ACTIVITY_SECTION: {
  key: LeadDrawerSectionKey;
  label: string;
  enabled: boolean;
} = {
  key: 'activities',
  label: '活动',
  enabled: true,
};

const BUSINESS_TRIP_SECTION: {
  key: LeadDrawerSectionKey;
  label: string;
  enabled: boolean;
} = {
  key: 'businessTrips',
  label: '相关出差',
  enabled: true,
};

const UNSUPPORTED_SECTION_COPY: Partial<Record<LeadDrawerSectionKey, string>> =
  {
    emails: '当前暂无邮件记录。',
    sms: '当前暂无短信记录。',
    companyInfo: '当前暂无工商信息。',
  };

const normalizeText = (value: unknown) =>
  typeof value === 'string' ? value.trim() : '';

const normalizeOptionalText = (value: unknown) =>
  normalizeText(value) || undefined;

const getErrorStatus = (error: unknown) => {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  const directStatus = (error as { status?: number }).status;
  if (typeof directStatus === 'number') {
    return directStatus;
  }

  const responseStatus = (error as { response?: { status?: number } }).response
    ?.status;
  if (typeof responseStatus === 'number') {
    return responseStatus;
  }

  const infoStatus = (error as { info?: { errorCode?: number } }).info
    ?.errorCode;
  if (typeof infoStatus === 'number') {
    return infoStatus;
  }

  return undefined;
};

const isForbiddenError = (error: unknown) => getErrorStatus(error) === 403;

const toPickerValue = (value?: string | null) =>
  value ? dayjs(value) : undefined;

const toIsoValue = (value?: Dayjs | null) => toApiDateTime(value);

const renderBoolTag = (value?: boolean) => (
  <Tag color={value ? 'green' : 'default'}>{value ? '已跟进' : '待跟进'}</Tag>
);

const renderTransformTag = (value?: boolean) => (
  <Tag color={value ? 'green' : 'default'}>{value ? '已转化' : '未转化'}</Tag>
);

function MetaItem({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className={styles.metaItem}>
      <span>{label}</span>
      <strong>{value || '-'}</strong>
    </div>
  );
}

function EmptyBlock({ description }: { description: string }) {
  return (
    <div className={styles.emptyBlock}>
      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={false} />
    </div>
  );
}

function SectionPanel({ title, hint, extra, children }: SectionPanelProps) {
  return (
    <DetailSurface
      title={title}
      extra={
        <Space size={8}>
          {hint ? <Text type="secondary">{hint}</Text> : null}
          {extra}
        </Space>
      }
      className={styles.sectionCard}
    >
      {children}
    </DetailSurface>
  );
}

export function useLeadDetailDrawer() {
  const ctx = useContext(LeadDetailDrawerContext);
  return (
    ctx || {
      openLead: ({ leadUuid }: LeadDrawerOpenInput) => {
        if (leadUuid) {
          history.push(`/zy-crm/lead/${leadUuid}`);
        }
      },
    }
  );
}

export function LeadDetailDrawerProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [leadUuid, setLeadUuid] = useState<string>();
  const [fallbackName, setFallbackName] = useState<ReactNode>();
  const [leadViewMode, setLeadViewMode] = useState<LeadScopedViewMode>();

  const openLead = useCallback((input: LeadDrawerOpenInput) => {
    const uuid = normalizeText(input.leadUuid);
    if (!uuid) {
      message.warning('该记录缺少线索 UUID，无法打开线索详情');
      return;
    }
    setLeadUuid(uuid);
    setFallbackName(input.leadName);
    setLeadViewMode(input.viewMode);
    setOpen(true);
  }, []);

  const value = useMemo(() => ({ openLead }), [openLead]);

  return (
    <LeadDetailDrawerContext.Provider value={value}>
      {children}
      <LeadDetailDrawer
        open={open}
        leadUuid={leadUuid}
        leadViewMode={leadViewMode}
        fallbackName={fallbackName}
        onClose={() => setOpen(false)}
      />
    </LeadDetailDrawerContext.Provider>
  );
}

export function LeadReferenceLink({
  leadUuid,
  leadName,
  fallbackName,
  emptyText = '-',
  canViewDetail = true,
  viewMode,
  style,
}: LeadReferenceLinkProps) {
  const { openLead } = useLeadDetailDrawer();
  const label = leadName || fallbackName || emptyText;
  if (!normalizeText(leadUuid) || canViewDetail === false) {
    const content = (
      <Text type="secondary" style={style}>
        {label}
      </Text>
    );
    if (normalizeText(leadUuid) && canViewDetail === false) {
      return <Tooltip title="无详情权限">{content}</Tooltip>;
    }
    return content;
  }
  return (
    <Button
      type="link"
      className={styles.leadLink}
      style={style}
      onClick={() => openLead({ leadUuid, leadName: label, viewMode })}
    >
      {label}
    </Button>
  );
}

function LeadDetailDrawer({
  open,
  leadUuid,
  leadViewMode,
  fallbackName,
  onClose,
}: {
  open: boolean;
  leadUuid?: string;
  leadViewMode?: LeadScopedViewMode;
  fallbackName?: ReactNode;
  onClose: () => void;
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error>();
  const [profile, setProfile] = useState<LeadDrawerProfile>();
  const [schedules, setSchedules] = useState<CalendarEventItem[]>([]);
  const [activeSection, setActiveSection] =
    useState<LeadDrawerSectionKey>('details');
  const [lookups, setLookups] = useState<LeadDrawerLookups>({
    areaOptions: [],
    customerOptions: [],
    industryOptions: [],
    sourceChannelOptions: [],
    memberNameMap: {},
  });
  const [attachmentCount, setAttachmentCount] = useState<number | undefined>();
  const [transformOpen, setTransformOpen] = useState(false);
  const [leadEditOpen, setLeadEditOpen] = useState(false);
  const [followUpOpen, setFollowUpOpen] = useState(false);
  const [editingFollowUp, setEditingFollowUp] =
    useState<LeadDrawerFollowUpItem | null>(null);
  const [_relatedMemberOptions, setRelatedMemberOptions] = useState<
    RelationOption[]
  >([]);
  const [_relatedMemberLoading, setRelatedMemberLoading] = useState(false);
  const [_relatedMemberLoadFailed, setRelatedMemberLoadFailed] =
    useState(false);
  const [scheduleEditor, setScheduleEditor] =
    useState<ScheduleEditorState | null>(null);
  const [scheduleSubmitting, setScheduleSubmitting] = useState(false);
  const [scheduleForm] = Form.useForm<CalendarEventFormValues>();
  const latestProfileRequestRef = useRef(0);
  const previousLeadUuidRef = useRef<string | undefined>(undefined);
  const {
    memberNameMap: sharedMemberNameMap,
    ensureMemberValue,
    searchMembers,
  } = useMemberDirectory();
  const [customerCreateOpen, setCustomerCreateOpen] = useState(false);
  const [contactCreateOpen, setContactCreateOpen] = useState(false);
  const [opportunityCreateOpen, setOpportunityCreateOpen] = useState(false);
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);
  const handleFollowUpTotalChange = useCallback((total: number) => {
    setProfile((current) => {
      if (!current) return current;
      const sections = current.sections.map((section) =>
        section.key === 'followUps' ? { ...section, count: total } : section,
      );
      return { ...current, sections };
    });
  }, []);

  useEffect(() => {
    if (!open) {
      previousLeadUuidRef.current = undefined;
      setSchedules([]);
      return;
    }
    setProfile(undefined);
    setError(undefined);
    setSchedules([]);
  }, [leadUuid, open]);

  const loadProfile = useCallback(async () => {
    if (!open || !leadUuid) return;
    const requestId = latestProfileRequestRef.current + 1;
    latestProfileRequestRef.current = requestId;
    setLoading(true);
    setError(undefined);
    try {
      const data = await queryLeadDrawerProfile(leadUuid, {
        skipErrorHandler: true,
        viewMode: leadViewMode,
      });
      if (requestId !== latestProfileRequestRef.current) {
        return;
      }
      setProfile(data);
      setSchedules(
        (data.activities || [])
          .filter((item) => item.sourceResource === 'calendarEvent')
          .map((item) => ({
            uuid: item.sourceUuid || item.key,
            title: item.title,
            start: item.time || '',
            end: item.dueTime || undefined,
            allDay: false,
            eventType: 'my_schedule',
            sourceType: 'custom',
            editable: true,
            description: item.description,
            sourceResource: item.sourceResource,
            sourceUuid: item.sourceUuid,
          })),
      );
    } catch (e) {
      if (requestId !== latestProfileRequestRef.current) {
        return;
      }
      const err = e instanceof Error ? e : new Error('线索详情加载失败');
      (err as DrawerRequestError).status = getErrorStatus(e);
      setError(err);
      setProfile(undefined);
      if (!isForbiddenError(e)) {
        message.error(err.message);
      }
    } finally {
      if (requestId === latestProfileRequestRef.current) {
        setLoading(false);
      }
    }
  }, [leadUuid, leadViewMode, open]);

  const loadSchedules = useCallback(async () => {
    if (!open || !leadUuid) {
      return;
    }
    try {
      const data = await queryCalendarEventsByBiz('lead', leadUuid);
      if (data?.length) {
        setSchedules(data);
      } else if (
        !(profile?.activities || []).some(
          (item) => item.sourceResource === 'calendarEvent',
        )
      ) {
        setSchedules([]);
      }
    } catch (error) {
      console.error(error);
      if (
        !(profile?.activities || []).some(
          (item) => item.sourceResource === 'calendarEvent',
        )
      ) {
        setSchedules([]);
      }
    }
  }, [leadUuid, open, profile?.activities]);

  useEffect(() => {
    void loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    void loadSchedules();
  }, [loadSchedules]);

  useEffect(() => {
    if (!open) return;
    const memberUuids = [
      normalizeText(profile?.lead?.ownerUserUuid),
      ...(profile?.related?.teamMembers?.list || []).map((item) =>
        normalizeText(item.memberUuid),
      ),
    ].filter(Boolean);

    void Promise.all([
      fetchAreaOptions(),
      fetchCustomerOptions(),
      fetchIndustryOptions(),
      fetchSourceChannelOptions(),
      Promise.all(
        Array.from(new Set(memberUuids)).map((memberUuid) =>
          ensureMemberValue(memberUuid).catch(() => undefined),
        ),
      ),
    ]).then(
      ([areaOptions, customerOptions, industryOptions, sourceChannelOptions]) =>
        setLookups({
          areaOptions,
          customerOptions,
          industryOptions,
          sourceChannelOptions,
          memberNameMap: sharedMemberNameMap,
        }),
    );
  }, [ensureMemberValue, open, profile, sharedMemberNameMap]);

  const lead = profile?.lead;
  const displayName = normalizeText(lead?.name) || fallbackName || '未命名线索';
  const sections = useMemo(() => {
    const baseSections = profile?.sections?.length
      ? profile.sections.filter(
          (section) => !['emails', 'sms'].includes(section.key),
        )
      : [
          {
            key: 'details' as const,
            label: '详细资料',
            enabled: true,
            count: 1,
          },
        ];

    const detailIndex = baseSections.findIndex(
      (section) => section.key === 'details',
    );
    const activityCount =
      Number(profile?.summary?.activityCount || 0) ||
      Number(profile?.activities?.length || 0);
    const nextSections = [...baseSections];
    const activitySection = {
      ...ACTIVITY_SECTION,
      count: activityCount,
    };
    if (!nextSections.some((section) => section.key === 'activities')) {
      if (detailIndex >= 0) {
        nextSections.splice(detailIndex + 1, 0, activitySection);
      } else {
        nextSections.unshift(activitySection);
      }
    } else {
      const activityIndex = nextSections.findIndex(
        (section) => section.key === 'activities',
      );
      nextSections[activityIndex] = {
        ...nextSections[activityIndex],
        ...activitySection,
        enabled: nextSections[activityIndex].enabled,
      };
    }
    if (!nextSections.some((section) => section.key === 'businessTrips')) {
      const taskIndex = nextSections.findIndex(
        (section) => section.key === 'tasks',
      );
      nextSections.splice(
        taskIndex >= 0 ? taskIndex + 1 : nextSections.length,
        0,
        BUSINESS_TRIP_SECTION,
      );
    }
    const attachmentIndex = nextSections.findIndex(
      (section) => section.key === 'attachments',
    );
    if (attachmentIndex >= 0) {
      nextSections[attachmentIndex] = {
        ...nextSections[attachmentIndex],
        count:
          attachmentCount ??
          nextSections[attachmentIndex].count ??
          profile?.related?.attachments?.total ??
          0,
      };
    }
    return normalizeDetailSections('lead', nextSections);
  }, [
    attachmentCount,
    profile?.activities?.length,
    profile?.related?.attachments?.total,
    profile?.sections,
    profile?.summary?.activityCount,
  ]);

  const displayedSection =
    sections.find((section) => section.key === activeSection)?.key ||
    defaultDetailSection('lead', sections);
  useEffect(() => {
    const currentLeadUuid = normalizeText(profile?.lead?.uuid);
    if (!currentLeadUuid) {
      return;
    }

    const sectionList = sections || [];
    const sectionKeys = new Set(
      sectionList.map((section) => section.key as LeadDrawerSectionKey),
    );
    const fallbackSection =
      (defaultDetailSection('lead', sectionList) as LeadDrawerSectionKey) ||
      'details';
    const isLeadChanged = previousLeadUuidRef.current !== currentLeadUuid;

    if (isLeadChanged) {
      previousLeadUuidRef.current = currentLeadUuid;
      setActiveSection(fallbackSection);
      return;
    }

    if (sectionKeys.size === 0) {
      return;
    }

    if (!sectionKeys.has(activeSection)) {
      setActiveSection(fallbackSection);
    }
  }, [activeSection, profile?.lead?.uuid, sections]);

  const effectiveCustomerUuid = normalizeText(
    lead?.customerUuid || lead?.sourceCustomerUuid,
  );
  const ownerText = resolveRelationText({
    value: lead?.ownerUserUuid,
    directName: lead?.ownerUserName || lead?.ownerName || lead?.ownerMemberName,
    nameMap: lookups.memberNameMap,
  });
  const loadRelatedMembers = useCallback(
    async (keyword?: string) => {
      setRelatedMemberLoading(true);
      try {
        const options = await searchMembers(keyword);
        setRelatedMemberOptions((prev) => {
          const map = new Map(prev.map((item) => [item.value, item]));
          options.forEach((item) => {
            map.set(item.value, item);
          });
          return Array.from(map.values());
        });
        setRelatedMemberLoadFailed(false);
        return options;
      } catch {
        setRelatedMemberLoadFailed(true);
        message.error('成员列表加载失败，请稍后重试');
        return [] as RelationOption[];
      } finally {
        setRelatedMemberLoading(false);
      }
    },
    [searchMembers],
  );

  const derivedActivities = useMemo(() => {
    const scheduleItems = schedules.map((item) => ({
      key: `calendarEvent-${item.uuid}`,
      type: 'calendarEvent',
      title: item.title,
      description: item.description,
      time: item.start,
      sourceResource: 'calendarEvent',
      sourceUuid: item.uuid,
    }));

    if (profile?.activities?.length) {
      const items = [...profile.activities, ...scheduleItems];
      const map = new Map<string, (typeof items)[number]>();
      items.forEach((item) => {
        const dedupeKey = `${item.sourceResource || item.type}:${item.sourceUuid || item.key}`;
        map.set(dedupeKey, item);
      });
      return Array.from(map.values()).sort((a, b) => {
        const left = dayjs(
          a.time || ('dueTime' in a ? a.dueTime : undefined) || 0,
        ).valueOf();
        const right = dayjs(
          b.time || ('dueTime' in b ? b.dueTime : undefined) || 0,
        ).valueOf();
        return right - left;
      });
    }
    const followUpItems = (profile?.related?.followUps?.list || []).map(
      (item) => ({
        key: `followUp-${item.uuid}`,
        type: 'followUp',
        title: item.content || '新增跟进记录',
        description: item.nextTime
          ? `下次联系 ${formatCrmDate(item.nextTime, 'date')}`
          : undefined,
        time: item.createTime,
        sourceResource: 'followUp',
        sourceUuid: item.uuid,
        status: item.followUpType,
      }),
    );
    const taskItems = (profile?.related?.tasks?.list || []).map((item) => ({
      key: `task-${item.uuid}`,
      type: 'task',
      title: item.title,
      description: item.description,
      time: item.dueTime || item.updatedAt,
      dueTime: item.dueTime,
      sourceResource: 'task',
      sourceUuid: item.uuid,
      status: item.status,
    }));

    return [...scheduleItems, ...followUpItems, ...taskItems].sort((a, b) => {
      const left = dayjs(
        a.time || ('dueTime' in a ? a.dueTime : undefined) || 0,
      ).valueOf();
      const right = dayjs(
        b.time || ('dueTime' in b ? b.dueTime : undefined) || 0,
      ).valueOf();
      return right - left;
    });
  }, [
    profile?.activities,
    profile?.related?.followUps?.list,
    profile?.related?.tasks?.list,
    schedules,
  ]);

  const openScheduleCreate = useCallback(() => {
    if (!leadUuid) {
      return;
    }
    scheduleForm.resetFields();
    scheduleForm.setFieldsValue(
      calendarEventFormDefaultValues({
        title: '',
        eventType: 'my_schedule',
        startTime: dayjs(),
        endTime: dayjs().add(1, 'hour'),
        allDay: false,
        ownerMemberUuid: normalizeOptionalText(lead?.ownerUserUuid),
        participantMemberUuids: [],
        relations: [createCalendarRelationFormValue('lead', leadUuid)],
      }),
    );
    void loadRelatedMembers();
    setScheduleEditor({ mode: 'create' });
  }, [lead?.ownerUserUuid, leadUuid, loadRelatedMembers, scheduleForm]);

  const closeScheduleEditor = useCallback(() => {
    setScheduleEditor(null);
    setScheduleSubmitting(false);
    scheduleForm.resetFields();
  }, [scheduleForm]);

  const submitScheduleEditor = useCallback(async () => {
    if (!leadUuid || !scheduleEditor) {
      return;
    }
    const values = await scheduleForm.validateFields();
    setScheduleSubmitting(true);
    try {
      const startTime = toApiDateTime(values.startTime);
      if (!startTime) {
        throw new Error('invalid start time');
      }
      const payload = {
        title: normalizeText(values.title),
        eventType: normalizeOptionalText(values.eventType) || 'my_schedule',
        startTime,
        endTime: toApiDateTime(values.endTime),
        allDay: Boolean(values.allDay),
        location: normalizeOptionalText(values.location),
        description: normalizeOptionalText(values.description),
        ownerMemberUuid: normalizeOptionalText(values.ownerMemberUuid),
        participantMemberUuids: Array.isArray(values.participantMemberUuids)
          ? (values.participantMemberUuids
              .map((item) => normalizeOptionalText(item))
              .filter(Boolean) as string[])
          : [],
        relations: normalizeCalendarRelationPayload(values.relations) || [
          {
            bizType: 'lead' as const,
            bizUuid: leadUuid,
            relationRole: 'primary' as const,
          },
        ],
      };

      if (scheduleEditor.mode === 'edit' && scheduleEditor.item?.uuid) {
        await updateCalendarEvent(scheduleEditor.item.uuid, payload);
        message.success('日程已更新');
      } else {
        await createCalendarEvent(payload);
        message.success('日程已创建');
      }
      closeScheduleEditor();
      await Promise.all([loadProfile(), loadSchedules()]);
    } catch (error) {
      console.error(error);
      message.error('保存失败，请稍后重试');
    } finally {
      setScheduleSubmitting(false);
    }
  }, [
    closeScheduleEditor,
    leadUuid,
    loadProfile,
    loadSchedules,
    scheduleEditor,
    scheduleForm,
  ]);

  return (
    <BusinessModal
      open={open}
      onCancel={handleClose}
      destroyOnHidden={false}
      title={null}
      detail
      aria-label="线索详情"
    >
      <div className={styles.shell} data-crm-shell>
        {loading && !profile ? (
          <div className={styles.loading}>
            <Skeleton active paragraph={{ rows: 12 }} />
          </div>
        ) : error ? (
          <div className={styles.loading}>
            <Space orientation="vertical" size={16} align="center">
              <Empty
                description={
                  isForbiddenError(error)
                    ? '你没有权限查看该线索详情'
                    : error.message || '线索详情加载失败'
                }
              />
              {isForbiddenError(error) ? null : (
                <Button onClick={() => void loadProfile()}>重试</Button>
              )}
            </Space>
          </div>
        ) : profile ? (
          <>
            <DetailSurface className={styles.heroCard}>
              <div className={styles.hero} data-crm-hero>
                <div className={styles.heroMain}>
                  <div className={styles.heroTopRow} data-crm-hero-row>
                    <div>
                      <div className={styles.titleRow} data-crm-title-row>
                        <Title
                          level={3}
                          className={styles.title}
                          data-crm-title
                        >
                          {displayName}
                        </Title>
                        {renderTransformTag(Boolean(lead?.transformStatus))}
                        {renderBoolTag(Boolean(lead?.followUpStatus))}
                      </div>
                      <Space wrap size={[8, 8]} className={styles.heroTags}>
                        {!lead?.ownerUserUuid ? (
                          <Tag color="gold">在线索池</Tag>
                        ) : null}
                        {effectiveCustomerUuid ? (
                          <Tag color="processing">已关联客户</Tag>
                        ) : (
                          <Tag>未关联客户</Tag>
                        )}
                      </Space>
                    </div>
                    <Space wrap>
                      {lead?.sourceOpportunityUuid ? (
                        <OpportunityReferenceLink
                          opportunityUuid={lead.sourceOpportunityUuid}
                          opportunityName="来源商机"
                        />
                      ) : null}
                      {lead?.sourceCustomerUuid ? (
                        <CustomerReferenceLink
                          customerUuid={lead.sourceCustomerUuid}
                          customerName="原客户"
                        />
                      ) : null}
                      <Button
                        type="primary"
                        onClick={() => setLeadEditOpen(true)}
                      >
                        编辑
                      </Button>
                      {!lead?.transformStatus ? (
                        <Button onClick={() => setTransformOpen(true)}>
                          转化
                        </Button>
                      ) : null}
                    </Space>
                  </div>
                  <div className={styles.metaGrid} data-crm-meta>
                    <MetaItem label="负责人" value={ownerText} />
                    <MetaItem label="手机号" value={lead?.mobile || '-'} />
                    <MetaItem
                      label="最近联系"
                      value={formatCrmDate(lead?.contactLastTime, 'date')}
                    />
                    <MetaItem
                      label="下次联系"
                      value={formatCrmDate(lead?.contactNextTime, 'date')}
                    />
                  </div>
                </div>
              </div>
            </DetailSurface>

            <div className={styles.workspace} data-crm-workspace>
              <aside className={styles.navPanel} data-crm-nav>
                <DetailSurface
                  className={styles.navCard}
                  data-crm-nav-surface
                  title="工作视角"
                >
                  <DetailNavigation
                    className={styles.navMenu}
                    selectedKeys={displayedSection ? [displayedSection] : []}
                    items={sections.map((section) => ({
                      key: section.key,
                      label: (
                        <div className={styles.menuLabel}>
                          <span>{section.label}</span>
                          {typeof section.count === 'number' ? (
                            <Text type="secondary">{section.count}</Text>
                          ) : null}
                        </div>
                      ),
                    }))}
                    onClick={({ key }) =>
                      setActiveSection(key as LeadDrawerSectionKey)
                    }
                  />
                </DetailSurface>
              </aside>

              <div className={styles.mainPanel} data-crm-main>
                {!displayedSection && (
                  <Empty description="当前无可查看的分区" />
                )}
                {displayedSection &&
                  renderSection(
                    displayedSection,
                    profile,
                    derivedActivities,
                    schedules,
                    lookups,
                    onClose,
                    () => setLeadEditOpen(true),
                    openScheduleCreate,
                    scheduleEditor,
                    scheduleForm,
                    scheduleSubmitting,
                    closeScheduleEditor,
                    submitScheduleEditor,
                    (count: number) => setAttachmentCount(count),
                    handleFollowUpTotalChange,
                    loadProfile,
                    () => {
                      setEditingFollowUp(null);
                      setFollowUpOpen(true);
                    },
                    (record) => {
                      setEditingFollowUp(record as LeadDrawerFollowUpItem);
                      setFollowUpOpen(false);
                    },
                  )}
              </div>

              <div className={styles.summaryPanel} data-crm-summary>
                <AiDetailPanel
                  bizType="lead"
                  bizUuid={leadUuid || ''}
                  summaryCounts={profile.summary}
                  initialSummary={profile.summaryPanel}
                  activeTab="summary"
                  contextTitle={profile.lead?.name}
                />
              </div>
            </div>
          </>
        ) : (
          <div className={styles.loading}>
            <Empty description={false} />
          </div>
        )}
      </div>

      <LeadTransformModal
        open={transformOpen}
        lead={lead as LeadItem | undefined}
        onClose={() => setTransformOpen(false)}
        onSuccess={() => void loadProfile()}
      />
      <LeadEditModal
        open={leadEditOpen}
        onOpenChange={setLeadEditOpen}
        leadUuid={lead?.uuid}
        currentMemberUuid={undefined}
        leadViewMode={leadViewMode}
        onSuccess={() => {
          setLeadEditOpen(false);
          void loadProfile();
        }}
      />
      <FollowUpCreateModal
        open={followUpOpen || Boolean(editingFollowUp)}
        title={editingFollowUp ? '编辑跟进记录' : '新建跟进记录'}
        context={{
          bizType: 1,
          bizUuid: lead?.uuid || '',
          bizName: lead?.name,
          defaultLeadUuids: lead?.uuid ? [lead.uuid] : undefined,
        }}
        editTarget={
          editingFollowUp ? { uuid: editingFollowUp.uuid } : undefined
        }
        defaultValues={
          editingFollowUp
            ? {
                followUpType: editingFollowUp.followUpType,
                content: editingFollowUp.content,
                nextTime: editingFollowUp.nextTime
                  ? dayjs(editingFollowUp.nextTime)
                  : undefined,
              }
            : undefined
        }
        onCancel={() => {
          setFollowUpOpen(false);
          setEditingFollowUp(null);
        }}
        onSuccess={() => {
          setFollowUpOpen(false);
          setEditingFollowUp(null);
          void loadProfile();
        }}
      />
      <CustomerEditModal
        open={customerCreateOpen}
        onOpenChange={setCustomerCreateOpen}
        currentMemberUuid={null}
        context={{
          leadName: lead?.name,
          mobile: lead?.mobile,
          telephone: lead?.telephone,
          email: lead?.email,
          qq: lead?.qq,
          wechat: lead?.wechat,
          areas: lead?.areas,
          remark: lead?.description,
        }}
        onSuccess={() => {
          setCustomerCreateOpen(false);
          void loadProfile();
        }}
      />
      <ContactEditModal
        open={contactCreateOpen}
        onOpenChange={setContactCreateOpen}
        currentMemberUuid={undefined}
        context={{
          leadName: lead?.name,
          customerUuid: effectiveCustomerUuid || undefined,
          mobile: lead?.mobile,
          telephone: lead?.telephone,
          email: lead?.email,
          qq: lead?.qq,
          wechat: lead?.wechat,
          areas: lead?.areas,
          remark: lead?.description,
        }}
        onSuccess={() => {
          setContactCreateOpen(false);
          void loadProfile();
        }}
      />
      <OpportunityEditModal
        open={opportunityCreateOpen}
        onOpenChange={setOpportunityCreateOpen}
        currentMemberUuid={undefined}
        initialValues={{
          name: lead?.name,
          customerUuid: effectiveCustomerUuid || undefined,
        }}
        onSuccess={() => {
          setOpportunityCreateOpen(false);
          void loadProfile();
        }}
      />
    </BusinessModal>
  );
}

function renderSection(
  key: LeadDrawerSectionKey,
  profile: LeadDrawerProfile,
  _activities: LeadDrawerProfile['activities'],
  _schedules: CalendarEventItem[],
  lookups: LeadDrawerLookups,
  _onClose: () => void,
  onEditLead: () => void,
  _onCreateSchedule: () => void,
  _scheduleEditor: ScheduleEditorState | null,
  _scheduleForm: ReturnType<typeof Form.useForm<CalendarEventFormValues>>[0],
  _scheduleSubmitting: boolean,
  _onCloseScheduleEditor: () => void,
  _onSubmitScheduleEditor: () => void,
  onAttachmentCountChange: (count: number) => void,
  onFollowUpTotalChange: (count: number) => void,
  onRefresh: () => void,
  _onCreateFollowUp: () => void,
  _onEditFollowUp: (record: LeadDrawerFollowUpItem) => void,
) {
  const related = profile.related || ({} as LeadDrawerProfile['related']);
  switch (key) {
    case 'activities':
      return (
        <SectionPanel title="活动">
          <ActivityPanel
            bizType="lead"
            bizUuid={profile.lead.uuid}
            bizName={profile.lead.name}
          />
        </SectionPanel>
      );
    case 'followUps':
      return (
        <SectionPanel title="跟进记录">
          <ActivityPanelFollowUps
            bizType="lead"
            bizUuid={profile.lead.uuid}
            bizName={profile.lead.name}
            onTotalChange={onFollowUpTotalChange}
            onRecordSaved={onRefresh}
          />
        </SectionPanel>
      );
    case 'details':
      return (
        <DetailsSection
          profile={profile}
          lookups={lookups}
          onEdit={onEditLead}
        />
      );
    case 'tasks':
      return (
        <CrmTaskRelationPanel
          title={detailSectionLabel('tasks')}
          sourceSceneUuid="lead"
          sourceObjectUuid={profile.lead.uuid}
          className={styles.sectionCard}
        />
      );
    case 'businessTrips':
      return (
        <BusinessTripRelationPanel
          title={detailSectionLabel('businessTrips')}
          objectType="lead"
          objectUuid={profile.lead.uuid}
          className={styles.sectionCard}
        />
      );
    case 'attachments':
      return (
        <AttachmentPanel
          bizType="lead"
          bizUuid={profile.lead.uuid}
          onDataLoaded={(page) => onAttachmentCountChange(page.total)}
        />
      );
    case 'teamMembers':
      return <TeamSection leadUuid={profile.lead.uuid} onRefresh={onRefresh} />;
    case 'referrals':
      return (
        <ReferralSection
          items={related.referrals || EMPTY_PAGE}
          currentLeadUuid={profile.lead.uuid}
          currentCustomerUuid={profile.lead.customerUuid}
          onRefresh={onRefresh}
        />
      );
    case 'operationLogs':
      return (
        <DetailOperationLogSection
          bizType="lead"
          bizUuid={profile.lead.uuid}
          container="section"
          sectionProps={{ className: styles.sectionCard }}
        />
      );
    default:
      return (
        <PlaceholderSection
          title={
            profile.sections.find((item) => item.key === key)?.label ||
            '相关信息'
          }
          description={UNSUPPORTED_SECTION_COPY[key] || '当前暂无可展示数据。'}
        />
      );
  }
}

function DetailsSection({
  profile,
  lookups,
  onEdit,
}: {
  profile: LeadDrawerProfile;
  lookups: LeadDrawerLookups;
  onEdit: () => void;
}) {
  const lead = profile.lead;
  const importDetail = profile.importDetail || lead.importDetail;
  const leadAttribution = lead as LeadItem & LeadImportAttributionFields;
  const sourceDetail = importDetail as
    | (LeadImportDetail & LeadImportAttributionFields)
    | null
    | undefined;
  const hasAttributionDetail = Boolean(
    sourceDetail ||
      leadAttribution.importSource ||
      leadAttribution.campaignUuid ||
      leadAttribution.campaignCode ||
      leadAttribution.campaignNameSnapshot ||
      leadAttribution.campaignTouchpointUuid ||
      leadAttribution.submitTime ||
      leadAttribution.requestId ||
      leadAttribution.utmSource ||
      leadAttribution.utmMedium ||
      leadAttribution.utmCampaign ||
      leadAttribution.clickId,
  );
  const attributionDetail: any = hasAttributionDetail
    ? {
        ...leadAttribution,
        ...sourceDetail,
        marketingCampaignUuid:
          sourceDetail?.marketingCampaignUuid || leadAttribution.campaignUuid,
        marketingCampaignName:
          sourceDetail?.marketingCampaignName ||
          sourceDetail?.campaignNameSnapshot ||
          leadAttribution.campaignNameSnapshot,
        campaignCode:
          sourceDetail?.campaignCode ||
          sourceDetail?.campaignCodeSnapshot ||
          leadAttribution.campaignCode,
        campaignTouchpointUuid:
          sourceDetail?.campaignTouchpointUuid ||
          leadAttribution.campaignTouchpointUuid,
        submitTime: sourceDetail?.submitTime || leadAttribution.submitTime,
        requestId: sourceDetail?.requestId || leadAttribution.requestId,
        utmSource: sourceDetail?.utmSource || leadAttribution.utmSource,
        utmMedium: sourceDetail?.utmMedium || leadAttribution.utmMedium,
        utmCampaign: sourceDetail?.utmCampaign || leadAttribution.utmCampaign,
        clickId: sourceDetail?.clickId || leadAttribution.clickId,
      }
    : null;
  const websiteLeadPayload = parseWebsiteLeadPayload(
    sourceDetail?.rawPayloadSnapshot,
  );
  const websiteLeadFields = Object.entries(websiteLeadPayload?.data || {});
  return (
    <SectionPanel
      title={detailSectionLabel('details')}
      hint="基础信息与当前跟进状态"
      extra={
        <Button size="small" onClick={onEdit}>
          编辑线索
        </Button>
      }
    >
      <ProDescriptions<LeadItem> column={2} dataSource={lead}>
        <ProDescriptions.Item dataIndex="name" label="标题" />
        <ProDescriptions.Item label="标签" span={2}>
          <EntityTagEditor
            entityType="lead"
            entityUuid={lead.uuid}
            tags={lead.tags}
            editable={lead.recordPermission?.actions?.edit !== false}
          />
        </ProDescriptions.Item>
        <ProDescriptions.Item dataIndex="companyName" label="公司名称" />
        <ProDescriptions.Item dataIndex="contactName" label="联系人" />
        <ProDescriptions.Item dataIndex="website" label="网址" span={2} />
        <ProDescriptions.Item label="负责人">
          {resolveRelationText({
            value: lead.ownerUserUuid,
            directName:
              lead.ownerUserName || lead.ownerName || lead.ownerMemberName,
            nameMap: lookups.memberNameMap,
          })}
        </ProDescriptions.Item>
        <ProDescriptions.Item
          dataIndex="followUpStatus"
          label="跟进状态"
          render={(value) => renderBoolTag(Boolean(value))}
        />
        <ProDescriptions.Item
          dataIndex="transformStatus"
          label="转化状态"
          render={(value) => renderTransformTag(Boolean(value))}
        />
        <ProDescriptions.Item label="系统评级">
          {lead.leadGrade ? `${lead.leadGrade} 级` : '未评级'}
          {lead.leadScore !== undefined && lead.leadScore !== null
            ? `（${lead.leadScore} 分）`
            : ''}
        </ProDescriptions.Item>
        <ProDescriptions.Item label="人工评级">
          {lead.manualLeadGrade ? `${lead.manualLeadGrade} 级` : '未评级'}
        </ProDescriptions.Item>
        <ProDescriptions.Item label="客户">
          <CustomerReferenceLink
            customerUuid={lead.customerUuid}
            customerName={resolveRelationLabel(
              lead.customerUuid,
              lookups.customerOptions,
              lead.customerName,
            )}
          />
        </ProDescriptions.Item>
        <ProDescriptions.Item label="关联联系人">
          <ContactReferenceLink
            contactUuid={lead.contactUuid || undefined}
            contactName={lead.linkedContactName}
          />
        </ProDescriptions.Item>
        <ProDescriptions.Item label="创建时间">
          {formatCrmDate((lead as LeadItem & { createdAt?: string }).createdAt)}
        </ProDescriptions.Item>
        <ProDescriptions.Item label="最近联系时间">
          {formatCrmDate(lead.contactLastTime, 'date')}
        </ProDescriptions.Item>
        <ProDescriptions.Item label="下次联系时间">
          {formatCrmDate(lead.contactNextTime, 'date')}
        </ProDescriptions.Item>
        <ProDescriptions.Item dataIndex="nextAction" label="下一步动作" />
        <ProDescriptions.Item dataIndex="mobile" label="手机号" />
        <ProDescriptions.Item dataIndex="telephone" label="电话" />
        <ProDescriptions.Item dataIndex="email" label="邮箱" />
        <ProDescriptions.Item dataIndex="wechat" label="微信" />
        <ProDescriptions.Item dataIndex="qq" label="QQ" />
        <ProDescriptions.Item label="地区">
          {lead.primaryAreaName ||
            (Array.isArray(lead.areaNames) ? lead.areaNames.join('、') : '') ||
            '-'}
        </ProDescriptions.Item>
        <ProDescriptions.Item label="行业">
          {resolveRelationLabel(
            lead.industryUuid,
            lookups.industryOptions,
            lead.industryName,
          )}
        </ProDescriptions.Item>
        <ProDescriptions.Item label="来源渠道">
          {resolveRelationLabel(
            lead.sourceChannel,
            lookups.sourceChannelOptions,
            undefined,
          )}
        </ProDescriptions.Item>
        <ProDescriptions.Item dataIndex="employeeCount" label="员工数" />
        <ProDescriptions.Item label="年收入">
          {formatMoney(lead.annualRevenue)}
        </ProDescriptions.Item>
        <ProDescriptions.Item label="导入来源">
          {lead.importSource || '-'}
        </ProDescriptions.Item>
        <ProDescriptions.Item label="线索金额">
          {formatMoney(lead.leadAmount)}
        </ProDescriptions.Item>
        <ProDescriptions.Item
          dataIndex="detailAddress"
          label="详细地址"
          span={2}
        />
        <ProDescriptions.Item
          dataIndex="contactLastContent"
          label="最近联系内容"
          span={2}
        />
        <ProDescriptions.Item dataIndex="description" label="描述" span={2} />
      </ProDescriptions>
      {attributionDetail ? (
        <ProCard title="留资来源" size="small" style={{ marginTop: 16 }}>
          <ProDescriptions<LeadImportDetail & LeadImportAttributionFields>
            column={2}
            dataSource={attributionDetail}
          >
            <ProDescriptions.Item label="来源应用">
              {attributionDetail.sourceApp || '-'}
            </ProDescriptions.Item>
            <ProDescriptions.Item label="来源记录 ID">
              {attributionDetail.sourceRecordId || '-'}
            </ProDescriptions.Item>
            {attributionDetail.sourceApp === 'lead-form' ? (
              <>
                <ProDescriptions.Item label="来源网站">
                  {formatWebsiteLeadValue(
                    websiteLeadPayload?.context?.metadata?.site,
                  )}
                </ProDescriptions.Item>
                <ProDescriptions.Item label="留资场景">
                  {websiteLeadPayload?.context?.scene || '-'}
                </ProDescriptions.Item>
                <ProDescriptions.Item label="来源页面" span={2}>
                  {websiteLeadPayload?.context?.sourceUrl || '-'}
                </ProDescriptions.Item>
              </>
            ) : null}
            <ProDescriptions.Item label="清洗状态">
              {attributionDetail.cleanStatus || '-'}
            </ProDescriptions.Item>
            <ProDescriptions.Item label="导入时间">
              {formatCrmDate(attributionDetail.importTime)}
            </ProDescriptions.Item>
            <ProDescriptions.Item label="清洗结论" span={2}>
              {attributionDetail.cleanResultSummary || '-'}
            </ProDescriptions.Item>
            <ProDescriptions.Item label="处理人">
              {attributionDetail.importOperator || '-'}
            </ProDescriptions.Item>
            <ProDescriptions.Item label="重复状态">
              {attributionDetail.duplicateStatus || '-'}
            </ProDescriptions.Item>
            <ProDescriptions.Item label="活动 UUID">
              {attributionDetail.marketingCampaignUuid || '-'}
            </ProDescriptions.Item>
            <ProDescriptions.Item label="活动编码">
              {attributionDetail.campaignCode || '-'}
            </ProDescriptions.Item>
            <ProDescriptions.Item label="活动名称快照">
              {attributionDetail.marketingCampaignName || '-'}
            </ProDescriptions.Item>
            <ProDescriptions.Item label="接入点 UUID">
              {attributionDetail.campaignTouchpointUuid || '-'}
            </ProDescriptions.Item>
            <ProDescriptions.Item label="提交时间">
              {formatCrmDate(attributionDetail.submitTime)}
            </ProDescriptions.Item>
            <ProDescriptions.Item label="requestId">
              {attributionDetail.requestId || '-'}
            </ProDescriptions.Item>
            <ProDescriptions.Item label="utmSource">
              {attributionDetail.utmSource || '-'}
            </ProDescriptions.Item>
            <ProDescriptions.Item label="utmMedium">
              {attributionDetail.utmMedium || '-'}
            </ProDescriptions.Item>
            <ProDescriptions.Item label="utmCampaign">
              {attributionDetail.utmCampaign || '-'}
            </ProDescriptions.Item>
            <ProDescriptions.Item label="clickId">
              {attributionDetail.clickId || '-'}
            </ProDescriptions.Item>
          </ProDescriptions>
          {attributionDetail.sourceApp === 'lead-form' &&
          websiteLeadFields.length ? (
            <ProDescriptions column={2} title="官网留资内容">
              {websiteLeadFields.map(([key, value]) => (
                <ProDescriptions.Item
                  key={key}
                  label={WEBSITE_LEAD_FIELD_LABELS[key] || key}
                  span={key === 'requirement' ? 2 : 1}
                >
                  {formatWebsiteLeadValue(value)}
                </ProDescriptions.Item>
              ))}
            </ProDescriptions>
          ) : null}
        </ProCard>
      ) : null}
    </SectionPanel>
  );
}

const referralTypeLabelMap: Record<string, string> = {
  contact: '联系人',
  customer: '客户',
  lead: '线索',
  opportunity: '商机',
};

function renderReferralObject(
  type?: string,
  uuid?: string,
  name?: string,
): ReactNode {
  const label = normalizeText(name) || normalizeText(uuid) || '-';
  switch (type) {
    case 'contact':
      return <ContactReferenceLink contactUuid={uuid} contactName={label} />;
    case 'customer':
      return <CustomerReferenceLink customerUuid={uuid} customerName={label} />;
    case 'lead':
      return <LeadReferenceLink leadUuid={uuid} leadName={label} />;
    case 'opportunity':
      return (
        <OpportunityReferenceLink
          opportunityUuid={uuid}
          opportunityName={label}
        />
      );
    default:
      return <Text>{label}</Text>;
  }
}

function ReferralSection({
  items,
  currentLeadUuid,
  currentCustomerUuid,
  onRefresh,
}: {
  items: LeadDrawerProfile['related']['referrals'];
  currentLeadUuid?: string;
  currentCustomerUuid?: string;
  onRefresh: () => void;
}) {
  const normalizedLeadUuid = normalizeText(currentLeadUuid);
  const normalizedCustomerUuid = normalizeText(currentCustomerUuid);
  const [incomingForm] = Form.useForm<IncomingReferralFormValues>();
  const [outgoingForm] = Form.useForm<OutgoingReferralFormValues>();
  const [incomingSubmitting, setIncomingSubmitting] = useState(false);
  const [outgoingSubmitting, setOutgoingSubmitting] = useState(false);
  const [contactOptions, setContactOptions] = useState<RelationOption[]>([]);
  const [customerOptions, setCustomerOptions] = useState<RelationOption[]>([]);
  const [leadOptions, setLeadOptions] = useState<RelationOption[]>([]);
  const [opportunityOptions, setOpportunityOptions] = useState<
    RelationOption[]
  >([]);
  const incomingItems = items.list.filter(
    (item) => normalizeText(item.targetUuid) === normalizedLeadUuid,
  );
  const outgoingItems = items.list.filter((item) => {
    const referrerUuid = normalizeText(item.referrerUuid);
    return (
      referrerUuid === normalizedLeadUuid ||
      (!!normalizedCustomerUuid && referrerUuid === normalizedCustomerUuid)
    );
  });

  const loadReferrerOptions = useCallback(
    async (
      type: ReferralReferrerType,
      keyword?: string,
      ensure?: Array<{ value: string; label?: string }>,
    ) => {
      const next =
        type === 'contact'
          ? await fetchContactOptions(keyword)
          : await fetchCustomerOptions(keyword);
      const ensured = (ensure || [])
        .filter((item) => normalizeText(item.value))
        .map((item) => ({
          value: normalizeText(item.value),
          label: normalizeText(item.label) || normalizeText(item.value),
        }));
      const merged = mergeOptions(next, ensured);
      if (type === 'contact') {
        setContactOptions(merged);
      } else {
        setCustomerOptions(merged);
      }
    },
    [],
  );

  const loadTargetOptions = useCallback(
    async (
      type: ReferralTargetType,
      keyword?: string,
      ensure?: Array<{ value: string; label?: string }>,
    ) => {
      const ensured = (ensure || [])
        .filter((item) => normalizeText(item.value))
        .map((item) => ({
          value: normalizeText(item.value),
          label: normalizeText(item.label) || normalizeText(item.value),
        }));
      if (type === 'contact') {
        setContactOptions(
          mergeOptions(await fetchContactOptions(keyword), ensured),
        );
      } else if (type === 'customer') {
        setCustomerOptions(
          mergeOptions(await fetchCustomerOptions(keyword), ensured),
        );
      } else if (type === 'lead') {
        setLeadOptions(mergeOptions(await fetchLeadOptions(keyword), ensured));
      } else if (type === 'opportunity') {
        setOpportunityOptions(
          mergeOptions(await fetchOpportunityOptions(keyword), ensured),
        );
      }
    },
    [],
  );

  const submitIncoming = useCallback(
    async (
      values: IncomingReferralFormValues,
      mode: 'create' | 'edit',
      record?: ReferralItem,
    ) => {
      if (!normalizedLeadUuid) {
        return;
      }
      const payload = {
        referrerType: values.referrerType as ReferralReferrerType,
        referrerUuid: normalizeText(values.referrerUuid),
        targetType: 'lead' as const,
        targetUuid: normalizedLeadUuid,
        referralTime: toIsoValue(values.referralTime),
        isPrimary: values.isPrimary ? 1 : 0,
        profitShareRatio:
          values.profitShareRatio === undefined ||
          values.profitShareRatio === null
            ? undefined
            : Number(values.profitShareRatio),
        remark: normalizeOptionalText(values.remark),
        status: record?.status ?? 1,
      };
      setIncomingSubmitting(true);
      try {
        if (mode === 'edit' && record?.uuid) {
          await updateReferral(record.uuid, payload);
        } else {
          await createReferral(payload);
        }
        message.success('介绍关系更新成功');
        await onRefresh();
      } finally {
        setIncomingSubmitting(false);
      }
    },
    [normalizedLeadUuid, onRefresh],
  );

  const submitOutgoing = useCallback(
    async (
      values: OutgoingReferralFormValues,
      mode: 'create' | 'edit',
      record?: ReferralItem,
    ) => {
      if (!normalizedLeadUuid) {
        return;
      }
      const referrerUuid =
        record?.referrerType === 'customer' && normalizedCustomerUuid
          ? normalizedCustomerUuid
          : normalizedLeadUuid;
      const referrerType =
        record?.referrerType === 'customer' && normalizedCustomerUuid
          ? ('customer' as const)
          : ('lead' as const);
      const payload = {
        referrerType,
        referrerUuid,
        targetType: values.targetType as ReferralTargetType,
        targetUuid: normalizeText(values.targetUuid),
        referralTime: toIsoValue(values.referralTime),
        remark: normalizeOptionalText(values.remark),
        status: record?.status ?? 1,
      };
      setOutgoingSubmitting(true);
      try {
        if (mode === 'edit' && record?.uuid) {
          await updateReferral(record.uuid, payload);
        } else {
          await createReferral(payload);
        }
        message.success('介绍关系更新成功');
        await onRefresh();
      } finally {
        setOutgoingSubmitting(false);
      }
    },
    [normalizedCustomerUuid, normalizedLeadUuid, onRefresh],
  );

  const removeReferral = useCallback(
    (record: ReferralItem) => {
      Modal.confirm({
        closable: { 'aria-label': '关闭' },
        keyboard: true,
        title: '确认删除介绍关系',
        content: `确认删除与${record.referrerName || record.targetName || record.uuid}相关的介绍关系吗`,
        okText: '删除',
        okButtonProps: { danger: true },
        cancelText: '取消',
        onOk: async () => {
          await deleteReferral(record.uuid);
          message.success('介绍关系删除成功');
          await onRefresh();
        },
      });
    },
    [onRefresh],
  );

  return (
    <UnifiedReferralSection
      title={detailSectionLabel('referrals')}
      hint={items.total ? `共 ${items.total} 条介绍关系` : '共 0 条介绍关系'}
      renderSection={({ title, hint, children }) => (
        <SectionPanel title={title} hint={hint}>
          {children}
        </SectionPanel>
      )}
      blocks={[
        {
          key: 'incoming',
          title: '谁介绍了当前线索',
          createButtonText: '新增介绍关系',
          items: incomingItems,
          emptyText: '',
          columns: [
            {
              title: '介绍人类型',
              dataIndex: 'referrerType',
              key: 'referrerType',
              width: 110,
              render: (value: ReferralItem['referrerType']) => (
                <Tag color="blue">
                  {referralTypeLabelMap[value] || value || '-'}
                </Tag>
              ),
            },
            {
              title: '介绍人',
              dataIndex: 'referrerName',
              key: 'referrerName',
              render: (_, record: ReferralItem) =>
                renderReferralObject(
                  record.referrerType,
                  record.referrerUuid,
                  record.referrerName,
                ),
            },
            {
              title: '介绍到当前线索',
              dataIndex: 'targetName',
              key: 'targetName',
              render: (_, record: ReferralItem) =>
                renderReferralObject(
                  record.targetType,
                  record.targetUuid,
                  record.targetName,
                ),
            },
            {
              title: '介绍时间',
              dataIndex: 'referralTime',
              key: 'referralTime',
              width: 180,
              render: (value?: string) => formatCrmDate(value) || '-',
            },
            {
              title: '是否主介绍人',
              dataIndex: 'isPrimary',
              key: 'isPrimary',
              width: 120,
              render: (value?: ReferralItem['isPrimary']) => (
                <Tag color={value ? 'gold' : 'default'}>
                  {value ? '主介绍人' : '普通介绍人'}
                </Tag>
              ),
            },
            {
              title: '备注',
              dataIndex: 'remark',
              key: 'remark',
              render: (value?: string) => value || '-',
            },
          ],
          editor: {
            form: incomingForm,
            createTitle: '新增介绍关系',
            editTitle: '编辑介绍关系',
            submitting: incomingSubmitting,
            buildInitialValues: (record?: ReferralItem) => ({
              referrerType: record?.referrerType || 'customer',
              referrerUuid: normalizeOptionalText(record?.referrerUuid),
              referralTime: toPickerValue(record?.referralTime),
              isPrimary: Boolean(record?.isPrimary),
              profitShareRatio:
                record?.profitShareRatio === undefined ||
                record?.profitShareRatio === null
                  ? undefined
                  : Number(record.profitShareRatio),
              remark: record?.remark,
            }),
            onOpen: (record) => {
              const referrerType =
                (record?.referrerType as ReferralReferrerType | undefined) ||
                'customer';
              const ensure = record?.referrerUuid
                ? [
                    {
                      value: normalizeText(record.referrerUuid),
                      label: record.referrerName,
                    },
                  ]
                : [];
              void loadReferrerOptions(referrerType, undefined, ensure);
            },
            onSubmit: ({ values, mode, record }) =>
              submitIncoming(values, mode, record),
            fields: [
              {
                type: 'radio',
                name: 'referrerType',
                label: '介绍人类型',
                rules: [{ required: true, message: '请选择介绍人类型' }],
                options: [
                  { label: '联系人', value: 'contact' },
                  { label: '客户', value: 'customer' },
                ],
                resetNames: ['referrerUuid'],
                onValueChange: (value) => {
                  if (value) {
                    void loadReferrerOptions(value as ReferralReferrerType);
                  }
                },
              },
              {
                type: 'select',
                name: 'referrerUuid',
                label: '介绍人',
                rules: [{ required: true, message: '请选择介绍人' }],
                options: ({ form }) =>
                  form.getFieldValue('referrerType') === 'contact'
                    ? contactOptions
                    : customerOptions,
                placeholder: ({ form }) =>
                  form.getFieldValue('referrerType')
                    ? '搜索并选择介绍人'
                    : '请先选择介绍人类型',
                disabled: ({ form }) => !form.getFieldValue('referrerType'),
                onLoadOptions: (keyword, { form, record }) => {
                  const type = form.getFieldValue('referrerType') as
                    | ReferralReferrerType
                    | undefined;
                  if (!type) return;
                  const ensure =
                    record?.referrerType === type && record?.referrerUuid
                      ? [
                          {
                            value: normalizeText(record.referrerUuid),
                            label: record.referrerName,
                          },
                        ]
                      : [];
                  void loadReferrerOptions(type, keyword, ensure);
                },
              },
              {
                type: 'date',
                name: 'referralTime',
                label: '介绍时间',
                showTime: true,
                placeholder: '请选择介绍时间',
              },
              {
                type: 'switch',
                name: 'isPrimary',
                label: '是否主介绍人',
                checkedChildren: '是',
                unCheckedChildren: '否',
              },
              {
                type: 'number',
                name: 'profitShareRatio',
                label: '分润比例',
                min: 0,
                max: 100,
                precision: 2,
                addonAfter: '%',
                placeholder: '请输入分润比例',
              },
              {
                type: 'textarea',
                name: 'remark',
                label: '备注',
                rows: 3,
                maxLength: 500,
                showCount: true,
              },
            ],
          },
          onDelete: removeReferral,
        },
        {
          key: 'outgoing',
          title: '当前线索后续转介绍到了哪里',
          createButtonText: '新增',
          items: outgoingItems,
          emptyText: '',
          columns: [
            {
              title: '目标类型',
              dataIndex: 'targetType',
              key: 'targetType',
              width: 110,
              render: (value: ReferralItem['targetType']) => (
                <Tag color="purple">
                  {referralTypeLabelMap[value] || value || '-'}
                </Tag>
              ),
            },
            {
              title: '后续介绍到',
              dataIndex: 'targetName',
              key: 'targetName',
              render: (_, record: ReferralItem) =>
                renderReferralObject(
                  record.targetType,
                  record.targetUuid,
                  record.targetName,
                ),
            },
            {
              title: '介绍时间',
              dataIndex: 'referralTime',
              key: 'referralTime',
              render: (value?: string) => formatCrmDate(value) || '-',
            },
            {
              title: '备注',
              dataIndex: 'remark',
              key: 'remark',
              render: (value?: string) => value || '-',
            },
          ],
          editor: {
            form: outgoingForm,
            createTitle: '新增介绍关系',
            editTitle: '编辑介绍关系',
            submitting: outgoingSubmitting,
            buildInitialValues: (record?: ReferralItem) => ({
              targetType: record?.targetType,
              targetUuid: normalizeOptionalText(record?.targetUuid),
              referralTime: toPickerValue(record?.referralTime),
              remark: record?.remark,
            }),
            onOpen: (record) => {
              if (!record?.targetType || !record?.targetUuid) {
                return;
              }
              void loadTargetOptions(record.targetType, undefined, [
                {
                  value: normalizeText(record.targetUuid),
                  label: record.targetName,
                },
              ]);
            },
            onSubmit: ({ values, mode, record }) =>
              submitOutgoing(values, mode, record),
            fields: [
              {
                type: 'radio',
                name: 'targetType',
                label: '目标类型',
                rules: [{ required: true, message: '请选择目标类型' }],
                options: [
                  { label: '联系人', value: 'contact' },
                  { label: '客户', value: 'customer' },
                ],
                resetNames: ['targetUuid'],
              },
              {
                type: 'select',
                name: 'targetUuid',
                label: '介绍出去的对象',
                rules: [{ required: true, message: '请选择目标对象' }],
                options: ({ form }) => {
                  const type = form.getFieldValue('targetType') as
                    | ReferralTargetType
                    | undefined;
                  if (type === 'contact') return contactOptions;
                  if (type === 'customer') return customerOptions;
                  if (type === 'lead') return leadOptions;
                  if (type === 'opportunity') return opportunityOptions;
                  return [];
                },
                placeholder: ({ form }) =>
                  form.getFieldValue('targetType')
                    ? '搜索并选择目标对象'
                    : '请先选择目标类型',
                disabled: ({ form }) => !form.getFieldValue('targetType'),
                onLoadOptions: (keyword, { form, record }) => {
                  const type = form.getFieldValue('targetType') as
                    | ReferralTargetType
                    | undefined;
                  if (!type) return;
                  const ensure =
                    record?.targetType === type && record?.targetUuid
                      ? [
                          {
                            value: normalizeText(record.targetUuid),
                            label: record.targetName,
                          },
                        ]
                      : [];
                  void loadTargetOptions(type, keyword, ensure);
                },
              },
              {
                type: 'date',
                name: 'referralTime',
                label: '介绍时间',
                showTime: true,
                placeholder: '请选择介绍时间',
              },
              {
                type: 'textarea',
                name: 'remark',
                label: '备注',
                rows: 3,
                maxLength: 500,
                showCount: true,
              },
            ],
          },
          onDelete: removeReferral,
        },
      ]}
    />
  );
}

function TeamSection({
  leadUuid,
  onRefresh,
}: {
  leadUuid: string;
  onRefresh: () => void;
}) {
  return (
    <TeamMembersPanel bizType="lead" bizUuid={leadUuid} onRefresh={onRefresh} />
  );
}

function _LogSection({
  title,
  items,
  emptyText,
}: {
  title: string;
  items: { list: LeadDrawerLogItem[]; total: number };
  emptyText: string;
}) {
  return (
    <SectionPanel
      title={title}
      hint={items.total ? `共 ${items.total} 条` : '—'}
    >
      {items.list.length ? (
        <Timeline
          items={items.list.map((item) => ({
            children: (
              <div className={styles.timelineItem}>
                <div className={styles.recordTitleRow}>
                  <span className={styles.recordTitle}>{item.title}</span>
                  <Text type="secondary">{formatCrmDate(item.time)}</Text>
                </div>
                {item.description ? (
                  <Paragraph className={styles.recordDescription}>
                    {item.description}
                  </Paragraph>
                ) : null}
              </div>
            ),
          }))}
        />
      ) : (
        <EmptyBlock description={emptyText} />
      )}
    </SectionPanel>
  );
}

function PlaceholderSection({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <SectionPanel title={title}>
      <EmptyBlock description={description} />
    </SectionPanel>
  );
}
